import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Wand2 } from 'lucide-react';
import { STORY_TEMPLATES } from '../../constants';
import MediaEditorModal from '../components/media/MediaEditorModal';
import AiGenerateModal from '../components/media/AiGenerateModal';
import { useSalonAiContext } from '../hooks/useSalonAiContext';
import { StoryTemplate, StoryContent } from '../types/media';

const MediaCreation: React.FC = () => {
  const { t } = useTranslation();
  const categoriesAll = t('media.categories.all');
  const [selectedCategory, setSelectedCategory] = useState(categoriesAll);
  const [activeTemplate, setActiveTemplate] = useState<StoryTemplate | null>(null);
  // Contenuti generati dall'AI con cui aprire l'editor precompilato.
  const [initialContent, setInitialContent] = useState<StoryContent | null>(null);
  const [isAiOpen, setIsAiOpen] = useState(false);

  const { salonContext } = useSalonAiContext(true);

  const CATEGORIES = useMemo(() => [
    categoriesAll,
    t('media.categories.promo'),
    t('media.categories.quote'),
    t('media.categories.product'),
    t('media.categories.seasonal'),
    t('media.categories.event')
  ], [t, categoriesAll]);

  const filteredTemplates = useMemo(() => {
    return STORY_TEMPLATES.filter(template =>
      selectedCategory === categoriesAll || template.category === selectedCategory
    );
  }, [selectedCategory, categoriesAll]);

  const openTemplate = (template: StoryTemplate) => {
    setInitialContent(null);
    setActiveTemplate(template);
  };

  const handleGenerated = (template: StoryTemplate, content: StoryContent) => {
    setInitialContent(content);
    setActiveTemplate(template);
  };

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <div className="flex items-center justify-between gap-4 pb-4 border-b border-gray-100">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{t('media.title')}</h1>
          <p className="text-gray-500 mt-1">{t('media.subtitle')}</p>
        </div>
        <button
          onClick={() => setIsAiOpen(true)}
          className="flex-shrink-0 flex items-center justify-center gap-2 w-12 md:w-auto md:px-6 h-12 bg-gray-900 text-white font-bold rounded-2xl shadow-lg hover:bg-black transition-all"
        >
          <Wand2 size={20} />
          <span className="hidden md:inline whitespace-nowrap">
            {t('media.generate_ai')}
          </span>
        </button>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-4 no-scrollbar">
        {CATEGORIES.map(cat => (
          <button
            key={cat}
            onClick={() => setSelectedCategory(cat)}
            className={`px-5 py-2 rounded-full font-bold text-sm transition-all whitespace-nowrap ${selectedCategory === cat
              ? 'bg-clip-600 text-white'
              : 'bg-white border border-gray-200 text-gray-600'
              }`}
          >
            {cat}
          </button>
        ))}
      </div>

      <motion.div layout className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
        <AnimatePresence mode='popLayout'>
          {filteredTemplates.map((template) => (
            <motion.div
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              key={template.id}
              onClick={() => openTemplate(template)}
              className="group relative aspect-[9/16] rounded-3xl overflow-hidden cursor-pointer shadow-sm border border-gray-100"
            >
              <img src={template.bgImage} alt={template.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors" />
              <div className="absolute inset-0 p-4 flex flex-col items-center justify-center text-center gap-2">
                <h3 className="text-white font-bold uppercase tracking-widest text-sm drop-shadow-md">{template.title}</h3>
                <span className="px-2 py-0.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
                  {template.category}
                </span>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      {/* Modal: conferma e configura la generazione AI */}
      <AiGenerateModal
        isOpen={isAiOpen}
        onClose={() => setIsAiOpen(false)}
        templates={filteredTemplates.length > 0 ? filteredTemplates : STORY_TEMPLATES}
        defaultTemplate={filteredTemplates[0] ?? null}
        context={salonContext}
        onGenerated={handleGenerated}
      />

      <AnimatePresence>
        {activeTemplate && (
          <MediaEditorModal
            template={activeTemplate}
            initial={initialContent}
            onClose={() => { setActiveTemplate(null); setInitialContent(null); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default MediaCreation;
